'use client';

import { Button, FormField } from '@shared/components/shadcn';
import { Spinner } from '@shared/icons';
import { EditData } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useFormContext, type UseFormReturn } from 'react-hook-form';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { PERSON_NAME_MAX_LENGTH } from '../../../form-controls/PersonName/schema';
import { TextInput } from '../../../form-controls/TextInput/TextInput';
import type { NameEditFormData } from '../schema';
import type { NameEditStepProps } from '../types';

function NameEditStep({ initialData }: NameEditStepProps) {
  const t = useTranslations('my_account.profile.profile_info');
  const tForm = useTranslations('form');
  const { goToNextStep, isLoading } = useMultistepForm();
  const form = useFormContext<NameEditFormData>();

  const watchedFirstName = form.watch('firstName');
  const watchedLastName = form.watch('lastName');
  const isFormChanged = watchedFirstName !== initialData.firstName || watchedLastName !== initialData.lastName;

  const handleContinue = async () => {
    if (!isFormChanged) {
      return;
    }

    const isValid = await form.trigger(['firstName', 'lastName'], { shouldFocus: false });
    if (!isValid) {
      return;
    }

    await goToNextStep();
  };

  const isContinueDisabled = !isFormChanged || isLoading;
  const textInputHelpMessageMode = 'focusout' as const;

  return (
    <div className="flex flex-col flex-1 min-h-0 p-6 h-full gap-6" data-track-container="Edit Name">
      <EditData size="extra-extra-large" data-testid="edit-data-icon" />

      <div className="space-y-2">
        <h4>{t('name_edit.update_name_title')}</h4>
        <p className="paragraph-explanatory text-muted-foreground">{t('name_edit.update_name_description')}</p>
      </div>

      <div className="space-y-4">
        <FormField<NameEditFormData>
          control={form.control}
          name="firstName"
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              id="firstName"
              label={tForm('labels.firstName')}
              maxLength={PERSON_NAME_MAX_LENGTH}
              helpMessage={fieldState.error?.message}
              helpMessageMode={textInputHelpMessageMode}
              helpMessageType={fieldState.error ? 'error' : undefined}
              inputTrackingProps={{
                'data-track-name': 'First Name',
                'data-track-privacy': 'Mask',
              }}
              helperTrackingProps={{
                'data-track-helper': 'First Name',
              }}
            />
          )}
        />

        <FormField<NameEditFormData>
          control={form.control}
          name="lastName"
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              id="lastName"
              label={tForm('labels.lastName')}
              maxLength={PERSON_NAME_MAX_LENGTH}
              helpMessage={fieldState.error?.message}
              helpMessageMode={textInputHelpMessageMode}
              helpMessageType={fieldState.error ? 'error' : undefined}
              inputTrackingProps={{
                'data-track-name': 'Last Name',
                'data-track-privacy': 'Mask',
              }}
              helperTrackingProps={{
                'data-track-helper': 'Last Name',
              }}
            />
          )}
        />
      </div>

      <div className="mt-auto shrink-0" data-track-container="Actions">
        <Button onClick={handleContinue} disabled={isContinueDisabled} className="w-full" data-track-name="Continue">
          {isLoading ? <Spinner size="small" className="animate-spin w-5" aria-label="Loading" /> : tForm('buttons.continue')}
        </Button>
      </div>
    </div>
  );
}

NameEditStep.validateAndUpdateData = async (formMethods: UseFormReturn<NameEditFormData>) => formMethods.trigger(['firstName', 'lastName']);

export { NameEditStep };
